/* eslint-disable react/no-array-index-key */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import RemoveIcon from '@mui/icons-material/Remove';
// eslint-disable-next-line no-restricted-imports
import {
  Collapse,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination as TablePaginationMui,
  TableRow,
  TableSortLabel
} from '@mui/material';
import { makeStyles, withStyles } from '@mui/styles';

import { useTheme } from 'contexts/ThemeContext';

import uuid from 'utils/uuid';

import CheckBox from 'components/Inputs/Checkbox';
import { IconButton } from 'components/Inputs/IconButton';
import { Item, Row } from 'components/Layout';
import { Paper } from 'components/Surfaces';

import { Text } from './Text';

const formatElem = elem => {
  if (typeof elem === 'string') {
    return elem.toUpperCase();
  }
  if (typeof elem === 'number') {
    return elem;
  }
  return '';
};

const descendingComparator = (a, b) => {
  const textA = formatElem(a);
  const textB = formatElem(b);

  if (textA > textB) {
    return -1;
  }
  if (textA < textB) {
    return 1;
  }
  return 0;
};

const getComparator = sens => {
  return sens === 'desc'
    ? (a, b) => descendingComparator(a, b)
    : (a, b) => -descendingComparator(a, b);
};

const sortRows = (rows, sortKey, orderBy, sortObjectKey) => {
  if (!sortKey) return rows;

  return rows.sort((rowA, rowB) => {
    const refA = sortObjectKey ? rowA[sortObjectKey] : rowA;
    const refB = sortObjectKey ? rowB[sortObjectKey] : rowB;

    const valA =
      Object.entries(refA).find(([key]) => key === sortKey)?.[1] || 0;
    const valB =
      Object.entries(refB).find(([key]) => key === sortKey)?.[1] || 0;

    return getComparator(orderBy)(valA, valB);
  });
};

const TablePaginationActions = props => {
  const useStyles = makeStyles({
    root: {
      flexShrink: 0,
      marginLeft: '30px'
    }
  });
  const theme = useTheme();
  const classes = useStyles();
  const { count, page, rowsPerPage, onPageChange = () => {} } = props;

  const handleFirstPageButtonClick = event => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = event => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = event => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = event => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className={classes.root}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  );
};

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired
};

export const TableWithColapse = ({
  header,
  subRowHeader,
  rows,
  style,
  headerColor,
  alternatedBackground,
  alternatedColsBackground,
  separator,
  noBorder,
  borderColor,
  borderSize,
  primary,
  subRowsBackgroundColor,
  secondary,
  elevation,
  noHeader,
  footer,
  stickyFooter,
  rowsFooter,
  rowHeight,
  headerHeight,
  stickyHeader,
  maxHeight,
  padding,
  maxWidthOffset,
  viewedRows,
  defaultSortKey,
  onRowClick,
  minCellWidth,
  rowsPerPageOptions,
  darkTitle,
  sortObjectKey,
  showErrorMessage,
  detailsOpened,
  actions,
  handleCollapse,
  showCheckboxes,
  onCheck,
  subRowBackgroundcolor,
  selectedItems
}) => {
  const { themeColors } = useTheme();
  const [orderBy, setOrderBy] = React.useState('desc');
  const [sortKey, setsortKey] = React.useState(
    defaultSortKey || header.find(e => e.sortable)?.key || null
  );
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(viewedRows);
  const [channels, setChannels] = React.useState(null);
  const [errorsDisplayed, setErrorsDisplayed] = React.useState([]);
  const [selected, setSelected] = React.useState([]);

  const headColor = themeColors[headerColor] || headerColor || null;
  const labelColor =
    headColor && !darkTitle ? themeColors.keep_light : themeColors.light;
  const primColor = themeColors[primary] || primary || themeColors.secondary;
  const secColor = themeColors[secondary] || secondary;

  useEffect(() => {
    setSelected(selectedItems);
  }, [selectedItems]);

  const handleSelectAllClick = event => {
    if (event.target.checked) {
      const newSelecteds = rows.map(row => row.id);
      setSelected(newSelecteds);
      onCheck(newSelecteds);
      return;
    }
    onCheck([]);
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    onCheck(newSelected);
    setSelected(newSelected);
  };

  const isSelected = id => selected.indexOf(id) !== -1;

  // eslint-disable-next-line consistent-return
  const onRequestSort = (key, sort) => {
    const isAsc = sortKey === key && orderBy === 'asc';
    const newOrderBy = isAsc ? 'desc' : 'asc';
    setOrderBy(newOrderBy);
    setsortKey(key);
    if (typeof sort === 'function') {
      return sort(rows, key, newOrderBy);
    }
  };

  const useStyles = makeStyles({
    root: {
      width: '100%',
      '& .MuiTableSortLabel-icon': {
        color: `${labelColor} !important`,
        fontSize: '13pt'
      },
      '& .MuiTableCell-root': {
        width: 'fit-content',
        padding
      },
      ...style
    },
    container: {
      maxHeight: maxHeight || 'auto',
      width: '100%',
      overflowX: 'auto',
      maxWidth: `calc(100vw - ${maxWidthOffset}px)`,
      '& .MuiTableSortLabel-icon': {
        fill: labelColor
      }
    },
    pagination: {
      color: themeColors.light
    }
  });

  const StyledTableRow = withStyles(theme => ({
    root: {
      height: rowHeight,
      backgroundColor: primColor,
      '&:nth-of-type(odd)': {
        backgroundColor: alternatedBackground
          ? secColor || theme.palette.action.hover
          : null
      }
    }
  }))(TableRow);

  useEffect(() => {
    setErrorsDisplayed(detailsOpened);
  }, [detailsOpened]);

  const StyledFooterCell = stickyFooter
    ? withStyles(() => ({
        root: {
          left: 0,
          bottom: 0,
          zIndex: 2,
          backgroundColor: headColor,
          color: labelColor
        }
      }))(TableCell)
    : TableCell;

  const classes = useStyles();

  const TablePagination = () => {
    const handleChangePage = (event, newPage) => {
      setPage(newPage);
    };
    const handleChangeRowsPerPage = event => {
      setRowsPerPage(+event.target.value);
      setPage(0);
    };

    const labelDisplayedRows = ({ from, to, count }) => {
      return `${from}–${to} sur ${count}`;
    };

    return (
      <TablePaginationMui
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        labelRowsPerPage="Lignes par page"
        labelDisplayedRows={labelDisplayedRows}
        page={page}
        onPageChange={handleChangePage}
        className={classes.pagination}
        // backIconButtonProps={('style': { color: themeColors.light })}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ActionsComponent={TablePaginationActions}
        rowsPerPageOptions={rowsPerPageOptions}
      />
    );
  };

  const iconDisplay = element => {
    return (
      <Item flex style={{ paddingLeft: '10px' }}>
        <IconButton
          aria-label="expand row"
          size="small"
          onClick={() => {
            const newObject = channels?.map(e =>
              e.media === element ? { ...e, collapse: !e.collapse } : e
            );
            setChannels(newObject);
          }}
        >
          {channels?.find(e => e.media === element)?.collapse ? (
            <RemoveIcon
              fontSize="small"
              style={{ color: themeColors.activation }}
            />
          ) : (
            <AddIcon
              fontSize="small"
              style={{ color: themeColors.activation }}
            />
          )}
        </IconButton>
      </Item>
    );
  };

  const displayCollapse = row => {
    return (
      <Item flex style={{ paddingLeft: '10px' }}>
        <IconButton
          aria-label="expand row"
          size="small"
          onClick={() => {
            handleCollapse(row);
          }}
        >
          {row.collapse ? (
            <ExpandLessIcon
              fontSize="small"
              style={{ color: themeColors.pml }}
            />
          ) : (
            <ExpandMoreIcon
              fontSize="small"
              style={{ color: themeColors.pml }}
            />
          )}
        </IconButton>
      </Item>
    );
  };

  const errorDisplay = (row, isSubRow) => {
    return (
      <TableCell
        display="grid"
        component="th"
        scope="row"
        key={uuid()}
        style={{
          padding: (isSubRow && padding) || '5px',
          borderRadius: '0px',
          borderBottom: separator
            ? `${borderSize}px solid ${borderColor || themeColors.tableBorder}`
            : 'none',
          width: `100%`,
          textAlign: 'center'
        }}
      >
        <Row spacing={0} justify="center" alignItems="center">
          <Row
            spacing={1}
            style={{
              backgroundColor: themeColors.light,
              margin: 5,
              borderRadius: 10
            }}
          >
            {row?.message?.split('|')?.map((message, index) => (
              <Item key={index} justify="flex-start">
                <Text color={themeColors.keep_light}>- {message}</Text>
              </Item>
            ))}
          </Row>
        </Row>
      </TableCell>
    );
  };

  const getSubRowBackgroundColor = isSubRow => {
    if (isSubRow) {
      return subRowBackgroundcolor || themeColors.primary;
    }
    return null;
  };

  const getCellBgColor = ({
    rowIndex,
    colsIndex,
    isSubRow,
    secondaryColor,
    primaryColor
  }) => {
    if (isSubRow && subRowsBackgroundColor) {
      return subRowsBackgroundColor;
    }

    if (alternatedColsBackground) {
      return colsIndex % 2
        ? secondaryColor || secondary
        : primaryColor || primary;
    }
    return rowIndex % 2 ? secondaryColor : primaryColor;
  };

  const subTable = (viewedItems, isSubRow = false) => {
    const hover = typeof onRowClick === 'function';
    const showError = showErrorMessage && isSubRow;
    return (
      <TableBody>
        {viewedItems.map((row, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={index}>
            <StyledTableRow
              id={row.id}
              key={uuid()}
              hover={hover}
              onClick={
                hover
                  ? () => {
                      onRowClick(row);
                    }
                  : undefined
              }
              style={{
                backgroundColor: getSubRowBackgroundColor(isSubRow),
                cursor: hover ? 'pointer' : 'auto'
              }}
            >
              {showCheckboxes && (
                <TableCell
                  style={{
                    width: '100px',
                    borderBottom: separator
                      ? `${borderSize}px solid ${borderColor ||
                          themeColors.tableBorder}`
                      : 'none'
                  }}
                  display="grid"
                  component="th"
                  scope="row"
                  key={uuid()}
                >
                  <CheckBox
                    checked={isSelected(row.id)}
                    onClick={event => handleClick(event, row.id)}
                  />
                </TableCell>
              )}
              {!showError &&
                (isSubRow ? subRowHeader : header).map(
                  (
                    {
                      key: columnKey,
                      format,
                      primaryColor,
                      secondaryColor,
                      width,
                      justify = 'center',
                      padding,
                      paddingSub,
                      colSpan = 1,
                      style = {}
                    },
                    subIndex
                  ) => {
                    const showCollapseIcon =
                      !row.collapse &&
                      row.subRows &&
                      row.subRows.length !== 0 &&
                      subIndex === 0;
                    const element = Object.entries(row).find(
                      ([key]) => key === columnKey
                    )?.[1];

                    return (
                      <TableCell
                        display="grid"
                        component="th"
                        scope="row"
                        key={uuid()}
                        colSpan={colSpan}
                        style={{
                          padding:
                            (isSubRow && paddingSub ? paddingSub : padding) ||
                            '5px',
                          borderRadius: '0px',
                          borderBottom: separator
                            ? `${borderSize}px solid ${borderColor ||
                                themeColors.tableBorder}`
                            : 'none',
                          width: width || `${100 / header.length}%`,
                          backgroundColor: getCellBgColor({
                            rowIndex: index,
                            colsIndex: subIndex,
                            isSubRow,
                            secondaryColor,
                            primaryColor
                          }),
                          textAlign: 'center',
                          ...style
                        }}
                      >
                        <Row
                          spacing={0}
                          justify="center"
                          alignItems="flex-start"
                        >
                          {columnKey === 'actions' && actions.length > 0 ? (
                            actions.map((action, index) => {
                              if (action?.display(row)) {
                                return (
                                  <Item
                                    key={index}
                                    style={{ padding: '0 5px' }}
                                  >
                                    <IconButton
                                      onClick={() => {
                                        action.onClick(row);
                                      }}
                                      aria-label={action.label}
                                      size="small"
                                    >
                                      {action.icon}
                                    </IconButton>
                                  </Item>
                                );
                              }
                              return null;
                            })
                          ) : (
                            <Item
                              xs
                              justify={justify}
                              id={`crm-cloningHistory-task-${row}-${subIndex.idName}`}
                            >
                              {typeof format === 'function' ? (
                                format(element, row, isSubRow)
                              ) : (
                                <Text>{element}</Text>
                              )}
                            </Item>
                          )}
                          {subIndex === header.length - 1 &&
                            row?.subRows?.length > 0 &&
                            handleCollapse &&
                            displayCollapse(row)}

                          {!handleCollapse &&
                            showCollapseIcon &&
                            iconDisplay(element)}
                        </Row>
                      </TableCell>
                    );
                  }
                )}
              {showError && errorDisplay(row, isSubRow)}
            </StyledTableRow>
            {row.subRows && row.subRows.length !== 0 && (
              <TableCell
                key={uuid()}
                align="center"
                style={{
                  padding: 0,
                  borderRadius: '0px',
                  borderBottom: 'none'
                }}
                colSpan={header.length + 1}
              >
                <Collapse
                  in={
                    showErrorMessage
                      ? errorsDisplayed.includes(row.status)
                      : channels?.find(e => e.media === row.media)?.collapse ||
                        row.collapse
                  }
                  timeout="auto"
                  unmountOnExit
                >
                  <MuiTable
                    style={{
                      backgroundColor: themeColors.secondary
                    }}
                  >
                    {subTable(row.subRows, true)}
                  </MuiTable>
                </Collapse>
              </TableCell>
            )}
          </React.Fragment>
        ))}
      </TableBody>
    );
  };

  const viewedItems = viewedRows
    ? sortRows(rows, sortKey, orderBy, sortObjectKey).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      )
    : sortRows(rows, sortKey, orderBy, sortObjectKey);
  if (channels === null) {
    setChannels(viewedItems);
  }

  return (
    <Paper
      elevation={elevation}
      className={classes.root}
      color={noBorder ? 'transparent' : null}
    >
      <Row spacing={0}>
        <Item>
          <TableContainer
            className={classes.container}
            style={{ borderRadius: '10px' }}
          >
            <MuiTable>
              {!noHeader && (
                <TableHead
                  style={stickyHeader ? { position: 'sticky', top: 0 } : {}}
                >
                  <TableRow style={{ height: headerHeight || rowHeight }}>
                    {showCheckboxes && (
                      <TableCell>
                        <CheckBox
                          indeterminate={
                            selected.length > 0 && selected.length < rows.length
                          }
                          checked={
                            rows.length > 0 && selected.length === rows.length
                          }
                          onChange={handleSelectAllClick}
                        />
                      </TableCell>
                    )}
                    {header.map(({ style = {}, ...e }) => {
                      return (
                        <TableCell
                          align={e?.align || e?.justify || 'center'}
                          key={uuid()}
                          colSpan={e.colSpan || 1}
                          style={{
                            width: e.width || `${100 / header.length}%`,
                            minWidth: minCellWidth,
                            justifyItems: e.justify || 'center',
                            padding: '10px 0px',
                            ...style,
                            backgroundColor: e.color || headColor
                          }}
                        >
                          {e.sortable ? (
                            <TableSortLabel
                              active={
                                sortKey === e.sortKey || sortKey === e.key
                              }
                              direction={
                                sortKey === e.sortKey || sortKey === e.key
                                  ? orderBy
                                  : 'desc'
                              }
                              IconComponent={ArrowDropDownIcon}
                              onClick={() =>
                                onRequestSort(e.sortKey || e.key, e.sort)
                              }
                            >
                              <Text
                                bold
                                color={labelColor}
                                style={{ ...(e.style || {}) }}
                              >
                                {e.label}
                              </Text>
                            </TableSortLabel>
                          ) : (
                            <>
                              {e.tooltip ? (
                                <Item
                                  flex
                                  style={{
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Text
                                    bold
                                    color={labelColor}
                                    style={{ ...(e.style || {}) }}
                                  >
                                    {e.label}
                                  </Text>
                                  {e.tooltip}
                                </Item>
                              ) : (
                                <Text
                                  bold
                                  color={labelColor}
                                  style={{ ...(e.style || {}) }}
                                >
                                  {e.label}
                                </Text>
                              )}
                            </>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
              )}
              {subTable(viewedItems, false)}
              {footer ? (
                <TableFooter
                  style={stickyFooter ? { position: 'sticky', bottom: 0 } : {}}
                >
                  <StyledTableRow key={uuid()}>
                    {header.map(({ key: columnKey, format, style, color }) => {
                      const element =
                        Object.entries(rowsFooter).find(
                          ([key]) => key === columnKey
                        )?.[1] || '';
                      return (
                        <StyledFooterCell
                          key={uuid()}
                          align="center"
                          style={{
                            borderBottom: 'none',
                            padding: '10px 0px',
                            borderRadius: '0px',
                            ...style,
                            backgroundColor: color || headColor
                          }}
                        >
                          <Text bold color={stickyFooter && labelColor}>
                            {typeof format === 'function' ? (
                              format(element, rowsFooter)
                            ) : (
                              <Text>{element}</Text>
                            )}
                          </Text>
                        </StyledFooterCell>
                      );
                    })}
                  </StyledTableRow>
                </TableFooter>
              ) : null}
            </MuiTable>
          </TableContainer>
        </Item>
      </Row>

      {!!viewedRows && (
        <Row>
          <TablePagination />
        </Row>
      )}
    </Paper>
  );
};

export default TableWithColapse;
